export interface SeoMetaConfig {
  title: string;
  description: string;
  url: string;
  image: string;
  imageAlt: string;
  type?: "website" | "article";
  siteName?: string;
  twitterHandle?: string;
  articleAuthor?: string;
  articlePublisher?: string;
  articleSection?: string;
  articlePublishedTime?: string;
  breadcrumbs?: { name: string; item: string }[];
  lang?: "en" | "es";
  alternateUrls?: { en: string; es: string };
}

export function injectMetaTags(config: SeoMetaConfig): () => void {
  const tags: (HTMLMetaElement | HTMLLinkElement | HTMLScriptElement)[] = [];
  const lang = config.lang || "en";
  const locale = lang === "es" ? "es_US" : "en_US";
  const alternateLocale = lang === "es" ? "en_US" : "es_US";

  const addMeta = (attrs: Record<string, string>) => {
    const el = document.createElement("meta");
    for (const [key, value] of Object.entries(attrs)) {
      el.setAttribute(key, value);
    }
    el.setAttribute("data-injected", "seo");
    document.head.appendChild(el);
    tags.push(el);
  };

  const addLink = (attrs: Record<string, string>) => {
    const el = document.createElement("link");
    for (const [key, value] of Object.entries(attrs)) {
      el.setAttribute(key, value);
    }
    el.setAttribute("data-injected", "seo");
    document.head.appendChild(el);
    tags.push(el);
  };

  // OpenGraph
  addMeta({ property: "og:type", content: config.type || "website" });
  addMeta({ property: "og:site_name", content: config.siteName || "Windows and Doors Repair LLC" });
  addMeta({ property: "og:locale", content: locale });
  addMeta({ property: "og:locale:alternate", content: alternateLocale });
  addMeta({ property: "og:url", content: config.url });
  addMeta({ property: "og:title", content: config.title });
  addMeta({ property: "og:description", content: config.description });
  addMeta({ property: "og:image", content: config.image });
  addMeta({ property: "og:image:width", content: "1200" });
  addMeta({ property: "og:image:height", content: "630" });
  addMeta({ property: "og:image:alt", content: config.imageAlt });

  // Twitter Card
  addMeta({ name: "twitter:card", content: "summary_large_image" });
  addMeta({ name: "twitter:site", content: config.twitterHandle || "@WindowsDoorsLLC" });
  addMeta({ name: "twitter:title", content: config.title });
  addMeta({ name: "twitter:description", content: config.description });
  addMeta({ name: "twitter:image", content: config.image });

  // Geo tags (Local SEO)
  addMeta({ name: "geo.region", content: "US-FL" });
  addMeta({ name: "geo.placename", content: "Miami" });
  addMeta({ name: "geo.position", content: "25.7617;-80.1918" });
  addMeta({ name: "ICBM", content: "25.7617, -80.1918" });

  // Article-specific
  if (config.type === "article") {
    if (config.articleAuthor) {
      addMeta({ property: "article:author", content: config.articleAuthor });
    }
    if (config.articlePublisher) {
      addMeta({ property: "article:publisher", content: config.articlePublisher });
    }
    if (config.articleSection) {
      addMeta({ property: "article:section", content: config.articleSection });
    }
    if (config.articlePublishedTime) {
      addMeta({ property: "article:published_time", content: config.articlePublishedTime });
    }
  }

  // Canonical link - points to itself (language-specific)
  addLink({ rel: "canonical", href: config.url });

  // Hreflang - self-referencing + alternate
  const currentHreflang = lang === "es" ? "es-US" : "en-US";
  addLink({ rel: "alternate", hreflang: currentHreflang, href: config.url });
  
  // Alternate language link
  if (config.alternateUrls) {
    const alternateLang = lang === "es" ? "en-US" : "es-US";
    const alternateUrl = lang === "es" ? config.alternateUrls.en : config.alternateUrls.es;
    addLink({ rel: "alternate", hreflang: alternateLang, href: alternateUrl });
    addLink({ rel: "alternate", hreflang: "x-default", href: config.alternateUrls.en });
  }

  // BreadcrumbList schema
  if (config.breadcrumbs && config.breadcrumbs.length > 0) {
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": config.breadcrumbs.map((crumb, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "name": crumb.name,
        "item": crumb.item,
      })),
    };
    const breadcrumbScript = document.createElement("script");
    breadcrumbScript.type = "application/ld+json";
    breadcrumbScript.id = "breadcrumb-schema";
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    breadcrumbScript.setAttribute("data-injected", "seo");
    document.head.appendChild(breadcrumbScript);
    tags.push(breadcrumbScript);
  }

  return () => {
    tags.forEach((tag) => {
      if (tag.parentNode) {
        tag.parentNode.removeChild(tag);
      }
    });
  };
}